'use client';

import { logger } from '@hen-site/lib/logger/client';
import {
  censorLoggedValue,
  censorString,
} from '@hen-site/lib/logger/telemetryDenylist';
import { BrowserAgent } from '@hen-site/lib/newrelic/types';

/**
 * Create an empty New Relic instance.
 * @returns BrowserAgent
 */
const createEmptyNewRelic = (): BrowserAgent => {
  return new Proxy({} as BrowserAgent, {
    get(_target, prop) {
      return (...args: unknown[]) => {
        logger.warn(
          censorString(`window.newrelic.${String(prop)}(${args.join(', ')})`),
        );
      };
    },
  });
};

/**
 * Check if New Relic is enabled and available in the window object.
 * @returns boolean
 */
const isNewRelicAvailable = () => {
  return typeof window !== 'undefined' && window.newrelic !== undefined;
};

/**
 * Client only wrapper for New Relic.
 * If New Relic is not enabled or not available in the window object,
 * return an empty New Relic instance.
 * @returns BrowserAgent
 */
function forwardCensored(agent: BrowserAgent): BrowserAgent {
  return new Proxy(agent, {
    get(target, prop, receiver) {
      const value: unknown = Reflect.get(target, prop, receiver);
      if (typeof value !== 'function') {
        return value;
      }
      const call = value as (...args: unknown[]) => unknown;
      return (...args: unknown[]) =>
        call.apply(
          target,
          args.map((arg) => censorLoggedValue(arg)),
        );
    },
  });
}

export const newrelic = isNewRelicAvailable()
  ? forwardCensored(window.newrelic)
  : createEmptyNewRelic();
